import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useAxios } from "hooks/useAxios";
import { USER_API } from "config/api-urls";
import {
  Workspace,
  WorkspacePayload,
  WorkspaceMember,
  WorkspaceMemberPayload,
  WorkspaceRole,
  UpdateWorkspaceDto,
  PendingInvitation,
} from "./workspace.types";
import { normalizeId } from "utils/genericUtilityFunctions";
import { catalogKeys } from "queries/profile.keys";
import { workspaceKeys } from "queries/workspace.keys";
import { updateCatalogInCache } from "hooks/useProfile/useProfileCache";
import {
  Permissions,
  canWorkspace,
  type WorkspacePermission,
} from "utils/permissions/permissionConstants";

// Transform workspace member payload to UI format
const transformWorkspaceMember = (member: WorkspaceMemberPayload): WorkspaceMember => {
  // For pending invitations, use invitee_email if email is not available
  const email = member.email || member.invitee_email || "";
  const firstName = member.first_name || "";
  const lastName = member.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  // For pending invitations, use display_name if available, otherwise null (UI will show "—")
  // For active members, use fullName or email as fallback
  const displayName =
    member.invitation_status === "pending" ? member.display_name || null : fullName || email;

  return {
    id: member.id,
    role: member.role,
    firstName,
    lastName,
    email,
    positionTitle: member.position_title || "",
    companyName: member.company_name || "",
    profileImagePath: member.profile_image_path || undefined,
    isActive: member.is_active,
    verified: member.verified,
    createdAt: member.created_at,
    updatedAt: member.updated_at,
    fullName,
    displayName,
    allProjectAccess: member.all_project_access ?? false,
    projectIds: member.project_ids ?? [],
    invitationStatus: member.invitation_status || undefined,
  };
};

// Transform workspace payload to UI format
const transformWorkspace = (workspace: WorkspacePayload): Workspace => {
  const members = workspace.members?.map(transformWorkspaceMember) || [];

  // Derive pending invitations from members array (single source of truth)
  // Filter members with invitation_status === "pending" and convert to PendingInvitation format
  const pendingInvitations = members
    .filter((member) => member.invitationStatus === "pending")
    .map((member): PendingInvitation => {
      // Find the original payload to get invitation-specific fields
      const originalMember = workspace.members?.find((m) => m.id === member.id);
      return {
        invitationId: originalMember?.invitation_id || member.invitationId || "",
        inviteeEmail: member.email,
        inviteeId: member.id,
        role: "viewer", // Default project role - project role not available in members array
        workspaceRole: member.role as string,
        allProjectAccess: member.allProjectAccess,
        projectIds: member.projectIds,
        status: "pending",
        expiresAt: originalMember?.expires_at || "",
        createdAt: member.createdAt,
        displayName: member.displayName || null,
        positionTitle: member.positionTitle || null,
      };
    });

  return {
    id: workspace.id,
    name: workspace.name,
    imagePath: workspace.image_path,
    contributeBudgetBenchmarks: workspace.contribute_budget_benchmarks ?? true,
    createdAt: workspace.created_at,
    updatedAt: workspace.updated_at,
    members,
    memberCount: workspace.member_count,
    pendingInvitations,
    permissions: workspace.permissions
      ? {
          permissions: workspace.permissions.permissions,
          role: workspace.permissions.role as WorkspaceRole,
          workspaceId: workspace.permissions.workspace_id,
          userId: workspace.permissions.user_id,
        }
      : undefined,
  };
};

// Hook for workspace settings management
export const useWorkspaceSettings = (workspaceId: string | number) => {
  const axios = useAxios({ allResponse: false });
  const queryClient = useQueryClient();
  const normalizedWorkspaceId = normalizeId(workspaceId);

  // Get workspace details with members and permissions
  const {
    data: workspaceData,
    isLoading: isLoadingWorkspace,
    isError: isWorkspaceError,
    error: workspaceError,
    refetch: refetchWorkspace,
  } = useQuery<WorkspacePayload>({
    queryKey: workspaceKeys.detail(normalizedWorkspaceId),
    queryFn: async () => {
      if (!normalizedWorkspaceId || normalizedWorkspaceId === "") {
        throw new Error("Workspace ID is required");
      }

      try {
        const response = await axios.get(USER_API.getWorkspace(normalizedWorkspaceId));
        return response.data.data || response.data;
      } catch (error: any) {
        if (error.response?.status === 403) {
          throw new Error("You don't have access to this workspace");
        }
        if (error.response?.status === 404) {
          throw new Error("Workspace not found");
        }
        throw error;
      }
    },
    enabled: !!normalizedWorkspaceId && normalizedWorkspaceId !== "",
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 403 || error?.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Transform workspace data
  const workspace = workspaceData ? transformWorkspace(workspaceData) : null;

  // Extract permissions and role
  const permissions = workspace?.permissions?.permissions || [];
  const userRole = workspace?.permissions?.role || null;

  // Permission checking functions
  const canWorkspacePermission = useCallback(
    (permission: WorkspacePermission): boolean => {
      return canWorkspace(permission, permissions);
    },
    [permissions]
  );

  const hasRole = useCallback(
    (role: WorkspaceRole): boolean => {
      return userRole === role;
    },
    [userRole]
  );

  // Update workspace mutation
  const updateWorkspaceMutation = useMutation({
    mutationFn: async (data: UpdateWorkspaceDto) => {
      const response = await axios.patch(USER_API.updateWorkspace(normalizedWorkspaceId), data);
      return response.data.data || response.data;
    },
    onSuccess: (updated) => {
      // Invalidate workspace detail to refetch fresh data
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(normalizedWorkspaceId) });

      updateCatalogInCache(queryClient, (catalog) => ({
        ...catalog,
        workspaces: catalog.workspaces.map((workspace) =>
          String(workspace.workspace_id) === String(normalizedWorkspaceId)
            ? {
                ...workspace,
                name: updated?.name ?? workspace.name,
                updated_at: updated?.updated_at ?? workspace.updated_at,
                contribute_budget_benchmarks:
                  updated?.contribute_budget_benchmarks ?? workspace.contribute_budget_benchmarks,
              }
            : workspace
        ),
      }));
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });

  // Update workspace member role mutation
  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: WorkspaceRole }) => {
      const response = await axios.patch(
        USER_API.updateWorkspaceMember(normalizedWorkspaceId, memberId),
        { role }
      );
      return response.data.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(normalizedWorkspaceId) });
    },
  });

  // Remove workspace member mutation
  // For pending invitations, use invitation_id (UUID string); for active members, use user id (string)
  const removeMemberMutation = useMutation({
    mutationFn: async ({ id, isInvitation }: { id: string; isInvitation: boolean }) => {
      const endpoint = isInvitation
        ? USER_API.cancelWorkspaceInvitation(normalizedWorkspaceId, id)
        : USER_API.removeWorkspaceMember(normalizedWorkspaceId, id);
      const response = await axios.delete(endpoint);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(normalizedWorkspaceId) });
    },
  });

  // Invite workspace member mutation
  const inviteMemberMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: WorkspaceRole }) => {
      const response = await axios.post(USER_API.inviteWorkspaceMember(normalizedWorkspaceId), {
        email,
        role,
      });
      return response.data.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(normalizedWorkspaceId) });
    },
  });

  return {
    // Workspace data
    workspace,
    isLoading: isLoadingWorkspace,
    isError: isWorkspaceError,
    error: workspaceError,
    refetch: refetchWorkspace,

    // Members data
    members: workspace?.members || [],
    membersCount: workspace?.members?.length || 0,

    // Pending invitations data
    pendingInvitations: workspace?.pendingInvitations || [],
    pendingInvitationsCount: workspace?.pendingInvitations?.length || 0,

    // Permissions
    permissions,
    userRole,
    canWorkspace: canWorkspacePermission,
    hasRole,

    // Permission checks (product-meaningful helpers)
    canManageMembers:
      canWorkspacePermission(Permissions.WORKSPACE_MEMBER_LIST) ||
      canWorkspacePermission(Permissions.WORKSPACE_MEMBER_INVITE) ||
      canWorkspacePermission(Permissions.WORKSPACE_MEMBER_UPDATE) ||
      canWorkspacePermission(Permissions.WORKSPACE_MEMBER_REMOVE),
    canManageBilling:
      canWorkspacePermission(Permissions.WORKSPACE_BILLING_READ) ||
      canWorkspacePermission(Permissions.WORKSPACE_BILLING_UPDATE),
    canCreateProjects: canWorkspacePermission(Permissions.WORKSPACE_PROJECT_CREATE),
    canReadWorkspace: canWorkspacePermission(Permissions.WORKSPACE_READ),
    canWriteWorkspace: canWorkspacePermission(Permissions.WORKSPACE_UPDATE),

    // Mutations
    updateWorkspace: updateWorkspaceMutation.mutate,
    updateWorkspaceAsync: updateWorkspaceMutation.mutateAsync,
    updateMemberRole: updateMemberRoleMutation.mutate,
    updateMemberRoleAsync: updateMemberRoleMutation.mutateAsync,
    removeMember: removeMemberMutation.mutate,
    removeMemberAsync: removeMemberMutation.mutateAsync,
    inviteMember: inviteMemberMutation.mutate,
    inviteMemberAsync: inviteMemberMutation.mutateAsync,

    // Mutation states
    isUpdatingWorkspace: updateWorkspaceMutation.isPending,
    isUpdatingMember: updateMemberRoleMutation.isPending,
    isRemovingMember: removeMemberMutation.isPending,
    isInvitingMember: inviteMemberMutation.isPending,

    // Mutation errors
    updateWorkspaceError: updateWorkspaceMutation.error,
    updateMemberError: updateMemberRoleMutation.error,
    removeMemberError: removeMemberMutation.error,
    inviteMemberError: inviteMemberMutation.error,
  };
};

export type UseWorkspaceSettingsResult = ReturnType<typeof useWorkspaceSettings>;
